import {
  NOTIFICATIONS,
  PERMISSIONS,
  ROLES,
  STATUSES,
  USERS,
} from '../../api/endpoints';
import { baseApi, listTags } from './baseApi';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUsersList: build.query({
      query: (params = {}) => ({ url: USERS.LIST, method: 'GET', params }),
      providesTags: (result) => listTags('User', result),
    }),
    getUserById: build.query({
      query: (id) => ({ url: USERS.BY_ID(id), method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    createUser: build.mutation({
      query: (userData) => ({ url: USERS.LIST, method: 'POST', data: userData }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    updateUser: build.mutation({
      query: ({ id, userData }) => ({
        url: USERS.BY_ID(id),
        method: 'PUT',
        data: userData,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),
    deleteUser: build.mutation({
      query: (id) => ({ url: USERS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),
    getUserStatus: build.query({
      query: () => ({
        url: STATUSES.LIST,
        method: 'GET',
        params: { 'conditions[model]': 'User' },
      }),
      providesTags: [{ type: 'Status', id: 'USER' }],
    }),
    getRoles: build.query({
      query: (params = {}) => ({ url: ROLES.LIST, method: 'GET', params }),
      providesTags: (result) => listTags('Role', result),
    }),
    getRoleById: build.query({
      query: (roleId) => ({ url: ROLES.BY_ID(roleId), method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'Role', id }],
    }),
    createRole: build.mutation({
      query: (body) => ({ url: ROLES.LIST, method: 'POST', data: body }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),
    updateRole: build.mutation({
      query: ({ id, ...body }) => ({
        url: ROLES.BY_ID(id),
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Role', id },
        { type: 'Role', id: 'LIST' },
      ],
    }),
    deleteRole: build.mutation({
      query: (id) => ({ url: ROLES.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),
    getNotifications: build.query({
      query: () => ({ url: NOTIFICATIONS.UNREAD, method: 'GET' }),
      providesTags: [{ type: 'Notification', id: 'UNREAD' }],
    }),
    getNotificationsList: build.query({
      query: (params = {}) => ({
        url: NOTIFICATIONS.LIST,
        method: 'GET',
        params,
      }),
      providesTags: (result) => listTags('Notification', result),
    }),
    markNotificationAsRead: build.mutation({
      query: (notificationId) => ({
        url: NOTIFICATIONS.BY_ID(notificationId),
        method: 'GET',
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'UNREAD' },
      ],
    }),
    markAllNotificationsAsRead: build.mutation({
      query: () => ({
        url: NOTIFICATIONS.MARK_ALL_READ,
        method: 'PATCH',
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'UNREAD' },
      ],
    }),
    getUsersByRole: build.query({
      query: (roles) => ({ url: USERS.BY_ROLE(roles), method: 'GET' }),
      providesTags: (_r, _e, roles) => [{ type: 'User', id: `role-${roles}` }],
    }),
    getUserByMultiRoles: build.query({
      query: (params = {}) => ({
        url: '/users/by-role',
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'User', id: 'BY_ROLE' }],
    }),
    updateUserDirectPermissions: build.mutation({
      query: ({ userId, permissions }) => ({
        url: USERS.DIRECT_PERMISSIONS(userId),
        method: 'PUT',
        data: { permissions },
      }),
      invalidatesTags: (_r, _e, { userId }) => [{ type: 'User', id: userId }],
    }),
    getAllPermissions: build.query({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({ url: PERMISSIONS.LIST, method: 'GET' });
        if (result.error?.status === 403) {
          return { data: [] };
        }
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      providesTags: [{ type: 'Permission', id: 'LIST' }],
    }),
    getPermissionsByRole: build.query({
      query: (roleId) => ({ url: ROLES.BY_ID(roleId), method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'Role', id }],
    }),
    updateRolePermissions: build.mutation({
      query: ({ roleId, payload }) => ({
        url: ROLES.BY_ID(roleId),
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (_r, _e, { roleId }) => [
        { type: 'Role', id: roleId },
        { type: 'Role', id: 'LIST' },
        { type: 'Permission', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetUsersListQuery,
  useLazyGetUsersListQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserStatusQuery,
  useLazyGetUserStatusQuery,
  useGetRolesQuery,
  useLazyGetRolesQuery,
  useGetRoleByIdQuery,
  useLazyGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetNotificationsListQuery,
  useLazyGetNotificationsListQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetUsersByRoleQuery,
  useLazyGetUsersByRoleQuery,
  useGetUserByMultiRolesQuery,
  useLazyGetUserByMultiRolesQuery,
  useUpdateUserDirectPermissionsMutation,
  useGetAllPermissionsQuery,
  useLazyGetAllPermissionsQuery,
  useGetPermissionsByRoleQuery,
  useLazyGetPermissionsByRoleQuery,
  useUpdateRolePermissionsMutation,
} = usersApi;
